<template>
  <div class="protocol">
    <div class="T_title" style="text-align: center">{{appName}} Personal Data Policy</div>
    <div class="T_content">
      {{appName}} Personal Data Policy
      <br /><br />
      When you run and use our mobile application, your personal data may be collected and processed. This document explains how {{appName}} gathers, utilizes and discloses user information while you access and enjoy our mobile-based services.
      <br /><br />
      <b>We recommend you read this document carefully to fully understand how your personal data is protected.</b>
      <br /><br />
      <b>This document covers the following contents:</b>
      <br /><br />
      1. How we collect and utilize your personal data
      <br /><br />
      2. Rules for sharing, transferring and public disclosure of your personal data
      <br /><br />
      3. Application of Cookies and third-party SDK tools
      <br /><br />
      4. Measures we take to protect your personal data
      <br /><br />
      5. Ways for you to manage your personal data
      <br /><br />
      6. Processing rules for minors' personal data
      <br /><br />
      7. Storage arrangements for your personal data
      <br /><br />
      8. Reminders for policy updates
      <br /><br />
      9. Ways to get in touch with our team
      <br /><br />
      <b>Overview</b>
      <br /><br />
      {{appName}} strictly keeps all collected user information confidential and has built a comprehensive personal data protection system to deliver secure and reliable services to every user.
      <br /><br />
      <b>1. How we collect and utilize your personal data </b>
      <br /><br />
      Personal data refers to all kinds of information recorded electronically or in other forms. Such information can identify a natural person or reflect personal activities individually or in combination with other data, including full name, date of birth, biometric data, residential address, contact details, chat records, account passwords, asset information, physical health data and transaction records.
      <br /><br />
      Sensitive personal data means information that may threaten personal and property safety, damage personal reputation or physical and mental health, or lead to unfair discrimination once leaked, illegally transferred or misused. It includes biometric data, bank account details, chat content, asset records, location information, health data, transaction records and personal information of users aged 14 and below.
      <br /><br />
      <b>You acknowledge and agree:</b>
      <br /><br />
      1. We develop various products and services to meet different user demands. Since users choose different service modules, the types and scope of collected personal data vary accordingly. Please refer to specific function descriptions for details.
      <br /><br />
      2. We continuously upgrade technologies to improve user experience. We may launch new or optimized functions from time to time, which may require new types of personal data or adjust existing usage purposes. We will explain relevant rules via policy updates, pop-up windows or page prompts, and collect data only after you give explicit consent. If you have any questions or suggestions, please reach out through our official contact channels and we will reply promptly.
      <br /><br />
      3. Data obtained from certain functions can be used for other services of ours. For example, we may use the information collected to push targeted ads and security reminders, or create user tags to deliver customized content. If we use your data beyond the original purpose, we will notify you and obtain your prior approval.
      <br /><br />
      <b>Special Note:</b> We provide user recommendation services. The nickname, age, gender, zodiac sign, occupation, company, hometown, regular activity locations, personal signature, uploaded avatars, photos and public posts you fill out may be visible or recommended to other users.
      <br /><br />
      <b> Service Modules and Corresponding Collected Data</b>
      In accordance with relevant industry regulations for mobile applications, {{appName}} is a social interaction application. Its core service is online instant messaging via text, images, voice and video. The required personal data includes registered email, nickname, gender, country and date of birth.
      <br /><br />
      Detailed collection rules for each function are listed below:
      <br /><br />
      1.Business function: Account Registration. To sign up for {{appName}}, you need to submit your email, nickname, age, date of birth, country and avatar. These data are used to complete account registration and enable social features. With your permission, we collect device information during installation, access and background operation, including device model, operating system version, device identifiers (IMEI/AndroidID/OAID/IDFA/IDFV/MEID/UDID/SIM card IMSI information/ICCID /SIM card status/VAID/AAID, etc.), serial number, MAC address, network data, device operating environment, installed application list, running processes and sensor information to ensure stable operation and service quality.
      <br /><br />
      2.Business function: Chat Service. We do not require extra personal data for normal chatting. We collect your account information to maintain stable service and protect account security. The chat interface supports image, voice and video message sending. You need to grant album and microphone permissions for these features. Refusing these permissions will not affect basic chat functions.
      <br /><br />
      3：Business function: Video Call. You need to enable cLuvina permission and microphone permission to use video call services. Without these permissions, video calls will not work properly.
      <br /><br />
      4：Business function: Browse Moments. No personal data is required for this function.
      <br /><br />
      5：Business function: View User Homepage. You can view others' uploaded images and videos without extra permissions. Video calls initiated from the user homepage require cLuvina permission and microphone permission.
      <br /><br />
      6.Business function: Profile Editing. You can edit avatar, nickname, age, date of birth and country. Changing avatar requires album permission, and disabling this permission will not affect other basic editing functions.
      <br /><br />
      7.Business function: Payment Service. The payment function is provided by our third-party payment partners. All payment-related data is collected by the payment institution, and we cannot access such information. Refusing to provide relevant data will only disable payment functions and will not affect other core services.
      <br /><br />
      8.Business function: Message Notification. We use your authorized auto-start permission to collect startup information and push timely notifications. You can turn off this permission in your phone settings.
      <br /><br />
      Our services may require you to enable cLuvina, album and microphone permissions. These permissions are not enabled by default. We will ask for your confirmation via independent pop-ups when enabling sensitive permissions. You can check and switch permission status at any time. Enabling permissions means you authorize us to collect corresponding data to provide services. Disabling permissions will stop subsequent data collection, but it will not affect data collected with your previous approval.
      <br /><br />
      You agree that we may send system messages, security verification emails and user research questionnaires to your registered email address.
      <br /><br />
      If you upload content containing other users' personal information, you must obtain legal authorization beforehand.
      <br /><br />
      We will keep and use your data during your service period. After you cancel your account or stop using certain functions, we will anonymize or delete relevant data in compliance with applicable laws.
      <br /><br />
      <b>Usage Rules for Collected Data</b>
      <br /><br />
      We only use your data for the stated purposes. Any cross-purpose usage or data obtained from third parties requires your prior consent. For data acquired from external partners, we will sign written agreements to ensure they collect data legally with user consent. Sensitive data requires additional confirmation before being shared. Third parties shall take full legal responsibility for illegal data sources. Our security team applies the same protection standards to externally obtained data.
      <br /><br />
      Exceptions to authorization: You agree that we may collect and use your personal data without your approval under the following legal circumstances, and you cannot request data correction, deletion or consent revocation:
      <br /><br />
      1. To fulfill legal obligations required by laws and regulations;
      <br /><br />
      2. Matters related to national security and national defense;
      <br /><br />
      3. Matters related to public safety, public health and public interests;
      <br /><br />
      4. Support for criminal investigation, prosecution, trial and judgment enforcement;
      <br /><br />
      5. To protect personal life and property rights when user consent cannot be obtained in a timely manner;
      <br /><br />
      6. Personal data released publicly by yourself;
      <br /><br />
      7. Data collected from legally public information sources
      <br /><br />
      In accordance with laws, we may process data via de-identification and anonymization so that individuals cannot be re-identified. Such anonymous data can be used for statistics, product optimization and algorithm training without further notice or consent. If we terminate service operation, we will stop collecting new data and announce data deletion or anonymization arrangements.
      <br /><br />
      <b>Content You Share</b>
      <br /><br />
      You can publish texts and photos via our services. Such content may contain ordinary or sensitive personal data. Please think carefully before public sharing. You can delete your published content in app settings. However, other users or unrelated third parties may still store your content independently.
      <br /><br />
      <b>2. Rules for sharing, transferring and public disclosure of your personal data</b>
      <br /><br />
      Data Sharing
      <br /><br />
      We will not share your personal data with external organizations or individuals except the following cases:
      <br /><br />
      1. Sharing with your explicit approval;
      <br /><br />
      2. Sharing required by laws, judicial proceedings or official authorities;
      <br /><br />
      3. Sharing with affiliated enterprises to provide joint services or personalized recommendations. We only share necessary data bound by this policy. We will apply for new approval if affiliated parties use sensitive data for other purposes.
      <br /><br />
      <b>3. Application of Cookies and third-party SDK tools</b>
      <br /><br />
      <b>Cookie</b>
      <br /><br />
      We store small cookie files on your device to ensure normal operation, optimize experience and push interested content. Cookies include identifiers and preference records. You can clear cookies in browser settings, but some dependent functions may become unavailable.
      <br /><br />
      <b>Third-party SDK</b>
      <br /><br />
      All SDKs are provided by our cooperative partners. We only know the permission list they apply for. Third parties collect user data independently and will inform us of collected sensitive information. The SDK list is as follows:
      <br /><br />
      Google firebase Push SDK
      <br /><br />
      Three-party payment SDK
      <br /><br />
      NetEase Yunxin SDK
      <br /><br />
      Shengwang SDK
      <br /><br />
      AppsFlyer SDK
      <br /><br />
      Facebook SDK
      <br /><br />
      HuaWei Push SDK
      <br /><br />
      Refusing third-party permissions will only disable corresponding SDK functions and will not affect other features of {{appName}}.
      <br /><br />
      4. Sharing with authorized partners: We may entrust qualified partners to provide services on our behalf. We only share necessary data for legal and specific purposes, and partners shall not use the data for other work.
      <br /><br />
      <b>Data Transfer</b>
      <br /><br />
      We will not transfer your personal data to external parties unless:
      <br /><br />
      1. You give explicit consent for data transfer;
      <br /><br />
      2. Enterprise merger, acquisition or bankruptcy leads to data handover. The new data holder must abide by this policy, otherwise new user consent is required.
      <br /><br />
      <b>Public Disclosure</b>
      <br /><br />
      We will only disclose your personal data publicly in the following situations:
      <br /><br />
      1. With your active choice and explicit approval;
      <br /><br />
      2. If you violate laws or platform rules, we may disclose relevant information legally to protect public interests.
      <br /><br />
      <b>4. Measures we take to protect your personal data</b>
      <br /><br />
      We follow legal standards to protect your data confidentiality and adopt the following measures:
      <br /><br />
      1. We deploy multiple security mechanisms to prevent illegal access, leakage and damage. We use encryption and anonymization technologies, implement access control and organize regular security training for staff.
      <br /><br />
      2. We have a full-cycle data security system and dedicated management teams. Once data leakage occurs, we will launch emergency plans and notify users via announcements or pop-ups.
      <br /><br />
      3. We only collect necessary data and retain it within the legal retention period.
      <br /><br />
      4. The internet cannot guarantee absolute transmission security. Please do not send private information via non-official channels. If your account or password is leaked, please contact Tantan custVolkie service in a timely manner.
      <br /><br />
      Reminder: Content posted in dynamic feeds or group chats may contain personal privacy. Please check carefully before publishing.
      <br /><br />
      <b>5. Ways for you to manage your personal data</b>
      <br /><br />
      <b> Personal data inquiry</b>
      <br /><br />
      You have the right to view your personal data except legal exceptions. You can check and edit data via [Me - Top Right - Settings].
      <br /><br />
      You can log in, enter personal center and click avatar to view or edit nickname, avatar, country, birthday and personal introduction.
      <br /><br />
      <b> Personal data correction</b>
      <br /><br />
      You can apply for correction if our recorded information is incorrect via the above entry.
      <br /><br />
      <b> Personal data deletion</b>
      <br /><br />
      Partial data can be deleted directly in settings. You can apply for full deletion under the following conditions:
      <br /><br />
      1. Our data processing violates relevant laws;
      <br /><br />
      2. Data is collected without your explicit consent;
      <br /><br />
      3. We seriously breach this agreement;
      <br /><br />
      4. You stop using services or apply for account cancellation;
      <br /><br />
      5. We permanently stop providing relevant services.
      <br /><br />
      <b> Adjust authorization scope</b>
      <br /><br />
      Basic functions require necessary data. You can enable or disable additional data collection permissions in settings, or contact {{appName}} custVolkie service.
      <br /><br />
      <b> 6. Processing rules for minors' personal data</b>
      <br /><br />
      <b> Minors are not allowed to register or use this application</b>
      <br /><br />
      Our services are not designed for users under 18 years old. We will not actively collect personal data from minors. Any accidentally obtained minor information will be deleted immediately. If relevant rules are updated in the future, we will obtain verified guardian approval before collecting minor data.
      <br /><br />
      <b> 7. Storage arrangements for your personal data</b>
      <br /><br />
      Our servers are deployed across multiple regions. Your data may be stored outside your current region. You agree to legal cross-region data transmission for service delivery.
      <br /><br />
      <b> 8. Reminders for policy updates</b>
      <br /><br />
      We reserve the right to revise this policy at any time. You can view the latest version on our official website. Platform notifications and web announcements are regarded as valid update notices. Your continued use of services means acceptance of revised clauses. Please stop using services if you disagree.
      <br /><br />
      <b> 9. Ways to get in touch with our team</b>
      <br /><br />
      For questions about this policy or privacy-related issues, please send emails to 【{{appName}}@{{appName}}.site】.
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {
    };
  },
  created() {
  }
};
</script>
<style scoped>
.protocol {
  font-size: 1rem;
  margin-bottom: 1rem;
  color: #727272;
  margin-left: 0.4rem;
  margin-right: 0.4rem;
}
.bt {
  font-size: 1rem;
  margin: 0.2rem 0px;
  color: #7f1146;
}
</style>